import {
  AccountApiError,
  type AccountApiErrorContext,
  isRecord,
  requestAccountJson
} from "./account-api";

export interface NamedWatchlist {
  id: string;
  name: string;
  tickers: string[];
  createdAt: string;
  updatedAt: string;
}

export class WatchlistsApiError extends AccountApiError {
  constructor(code: string, message: string, context: AccountApiErrorContext) {
    super(code, message, context);
    this.name = "WatchlistsApiError";
  }
}

const createError = (code: string, message: string, context: AccountApiErrorContext) =>
  new WatchlistsApiError(code, message, context);

function parseWatchlist(value: unknown, endpoint: string): NamedWatchlist {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    typeof value.name !== "string" ||
    !Array.isArray(value.tickers) ||
    typeof value.createdAt !== "string" ||
    typeof value.updatedAt !== "string"
  ) {
    throw new WatchlistsApiError("watchlists_invalid", "Watchlist response has an invalid shape", {
      endpoint
    });
  }
  return {
    id: value.id,
    name: value.name,
    tickers: value.tickers.map((ticker) => String(ticker).toUpperCase()),
    createdAt: value.createdAt,
    updatedAt: value.updatedAt
  };
}

export async function loadNamedWatchlists(): Promise<NamedWatchlist[]> {
  const endpoint = "/api/watchlists";
  const payload = await requestAccountJson({
    endpoint,
    unavailableCode: "watchlists_unavailable",
    httpCode: "watchlists_http",
    invalidCode: "watchlists_invalid",
    createError
  });
  if (!isRecord(payload) || !Array.isArray(payload.watchlists)) {
    throw new WatchlistsApiError("watchlists_invalid", "Watchlists response has an invalid shape", {
      endpoint
    });
  }
  return payload.watchlists.map((item) => parseWatchlist(item, endpoint));
}

export async function createNamedWatchlist(name: string): Promise<NamedWatchlist> {
  const endpoint = "/api/watchlists";
  const payload = await requestAccountJson({
    endpoint,
    init: {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name })
    },
    unavailableCode: "watchlists_unavailable",
    httpCode: "watchlists_http",
    invalidCode: "watchlists_invalid",
    createError
  });
  if (!isRecord(payload)) {
    throw new WatchlistsApiError("watchlists_invalid", "Create watchlist response invalid", {
      endpoint
    });
  }
  return parseWatchlist(payload.watchlist, endpoint);
}

export async function deleteNamedWatchlist(id: string): Promise<void> {
  const endpoint = `/api/watchlists/${encodeURIComponent(id)}`;
  await requestAccountJson({
    endpoint,
    init: { method: "DELETE" },
    unavailableCode: "watchlists_unavailable",
    httpCode: "watchlists_http",
    invalidCode: "watchlists_invalid",
    createError
  });
}

export async function mutateNamedWatchlistTicker(
  id: string,
  ticker: string,
  action: "add" | "remove"
): Promise<NamedWatchlist> {
  const endpoint = `/api/watchlists/${encodeURIComponent(id)}`;
  const payload = await requestAccountJson({
    endpoint,
    init: {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ticker, action })
    },
    unavailableCode: "watchlists_unavailable",
    httpCode: "watchlists_http",
    invalidCode: "watchlists_invalid",
    createError
  });
  if (!isRecord(payload)) {
    throw new WatchlistsApiError("watchlists_invalid", "Watchlist mutation response invalid", {
      endpoint
    });
  }
  return parseWatchlist(payload.watchlist, endpoint);
}
